import core from '@gin-betting/core';
import favoriteMarkets from '@gin-betting/favorite-markets';
import { persistReducer, persistStore } from '@gin-betting/persist';
import Immutable from 'immutable';
import { forEach, map, values } from 'lodash';
import {
    applyMiddleware,
    compose,
    Middleware,
    MiddlewareAPI,
    Store,
} from 'redux';
import { createEpicMiddleware } from 'redux-observable';
import createSentryMiddleware from 'redux-sentry-middleware';
import thunk from 'redux-thunk';

import { matchListRoutes } from 'app-constants';
import bannersModule from 'modules/banners';
import favorite from 'modules/favorite';
import matchList from 'modules/match-list';
import promo from 'modules/promo';
import ui from 'modules/ui';
import createStore from 'services/createStore';
import gc from 'services/garbage-collector';
import UserDevice from 'services/user-device';
import { State } from 'types';
import createRootReducer from './createRootReducer';
import { bannerDevMiddleware } from './devMiddleware';

const {
    services: {
        middlewareRegistry,
        reducerRegistry,
        rootEpic,
        registerEpic,
        sentryHub,
    },
    entities,
    viewport: {
        selectors: { selectIsCompact },
    },
} = core;

const { middleware: gcMiddleware } = gc;

const dynamicMiddlewares = ({ getState, dispatch }: MiddlewareAPI<any>) => (
    next: any
) => (action: any) => {
    const middlewareAPI = {
        getState,
        dispatch: (act: any) => dispatch(act),
    };

    // TODO: optimize access to dynamically added middlewares;
    //       this occurs on every dispatched action
    const chain = map(values(middlewareRegistry.getEntries()), middleware =>
        middleware(middlewareAPI)
    );

    return compose<any>(...chain)(next)(action);
};

const persistConfig = {
    key: 'root',
    whitelist: [],
    stateReconciler: false as const,
    debug: true,
};

export default function configureStore(): Store<State> {
    let middlewares: Middleware[] = [thunk];

    if (__DEV__) {
        const reduxLog = localStorage.getItem('redux-log');

        middlewareRegistry.register('devMiddleware', bannerDevMiddleware);

        if (reduxLog !== null) {
            import(/* webpackChunkName: "devtools" */ 'redux-logger')
                .then(m => {
                    const loggerMiddleware = m.createLogger({
                        collapsed: true,
                        timestamp: false,
                        logErrors: true,
                        predicate: (getState, action) => {
                            if (
                                [
                                    'async/SUBSCRIBE_TO_UPDATES',
                                    'async/UNSUBSCRIBE_TO_UPDATES',
                                ].includes(action.type)
                            ) {
                                return false;
                            }

                            return (
                                reduxLog === '*' ||
                                action.type.includes(reduxLog)
                            );
                        },
                    });

                    middlewareRegistry.register('logger', loggerMiddleware);
                })
                .catch(err => console.error(err));
        }
    }

    middlewares = middlewares.concat([
        dynamicMiddlewares,
        matchList.pagination.middleware(matchListRoutes),
        matchList.middleware(),
        entities.middleware(),
        core.appServiceLocator.get('apiMiddleware'),
        core.appServiceLocator.get('subscriptionMiddleware'),
        createEpicMiddleware(rootEpic as any),
        gcMiddleware(),
    ]);

    middlewares.push(
        createSentryMiddleware(sentryHub as any, {
            stateTransformer: (state: State) => {
                return state.delete('entities');
            },
        })
    );

    // @ts-ignore: Ignore window typings to access devtools
    const devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION__;

    const store = createStore<State>(
        persistReducer<State>(
            persistConfig,
            createRootReducer(reducerRegistry.getEntries())
        ),
        compose<any>(
            applyMiddleware(...middlewares),
            devToolsExtension
                ? devToolsExtension({
                      maxAge: getDevtoolActionsMaxAge(),
                      serialize: {
                          immutable: Immutable,
                      },
                  })
                : (f: any) => f
        ),
        undefined
    );

    const { requestLocalSport, ...otherFavoriteEpics } = favorite.epics;
    const { makeSyncGamblerSettingsByPlatform, ...otherUIEpics } = ui.epics;
    const {
        epics: { makeRequestBanners },
        utils: { getBanners },
    } = bannersModule;

    // TODO: fix epic type, 'Epic' is not assignable to type 'TypedEpic'
    registerEpic(requestLocalSport as any);
    forEach(otherFavoriteEpics, epic => registerEpic(epic));
    forEach(otherUIEpics, epic => registerEpic(epic));
    forEach(matchList.epics, epic => registerEpic(epic));
    registerEpic(makeRequestBanners(getBanners));
    forEach(promo.epics, epic => registerEpic(epic));
    forEach(favoriteMarkets.epics, epic => registerEpic(epic));

    const isCompact = selectIsCompact(store.getState(), {});

    registerEpic(
        makeSyncGamblerSettingsByPlatform(
            UserDevice.getInstance()?.isDeviceMobile() || isCompact
        )
    );

    const persistor = persistStore(store);

    reducerRegistry.onRegistryChange(newReducers => {
        store.replaceReducer(
            persistReducer(persistConfig, createRootReducer(newReducers))
        );

        if (
            Object.keys(newReducers).some(
                // NOTE: check strictly for "false" value, not just empty
                // @ts-ignore: Custom prop "_isPersisted" is missing in lib typings
                name => newReducers[name]?._isPersisted === false
            )
        ) {
            persistor.persist();
        }
    });

    enableHotLoader(store);

    return store;
}

function enableHotLoader(store: Store<any>) {
    const isDev = typeof __DEV__ === 'string';

    if (!isDev) return;

    const { hot } = module as any;

    if (hot) {
        hot.accept('./createRootReducer', () => {
            // eslint-disable-next-line
            const createNextRootReducer: any = require('./createRootReducer');
            store.replaceReducer(
                persistReducer(persistConfig, createNextRootReducer())
            );
        });
    }
}

function getDevtoolActionsMaxAge() {
    const defaultAge = 100;

    try {
        const age = localStorage && localStorage.getItem('devtool-max-age');

        return age ? +age : defaultAge;
    } catch (e) {
        return defaultAge;
    }
}
